import { computed, type MaybeRef, type Ref, ref, unref } from "vue";
import {
  INTERNATIONAL_COLLEGE_STUDENT_ID,
  INTERNATIONAL_HIGH_SCHOOL_STUDENT_ID,
  US_COLLEGE_STUDENT_ID,
  US_GRADUATE_STUDENT_ID,
  US_HIGH_SCHOOL_STUDENT_ID,
  WORKING_PROFESSIONAL_ID,
} from "@/constants";

interface DisplaySettingsResponse {
  isUsCollegeStudent: Ref<boolean>;
  hasHomeInstitution: Ref<boolean>;
  showLearnerTypes: Ref<boolean>;
  showSchools: Ref<boolean>;
  showMajorMinors: Ref<boolean>;
  showGpa: Ref<boolean>;
  showExpectedGraduation: Ref<boolean>;
  showLanguages: Ref<boolean>;
  showYearInSchool: Ref<boolean>;
  showSchoolStanding: Ref<boolean>;
  showPlanToPay: Ref<boolean>;
  showAddress: Ref<boolean>;
  updateLearnerTypeId: (id: MaybeRef<string>) => void;
}

interface RequiredSession {
  is_gpa_required: MaybeRef<boolean>;
  is_personalized: MaybeRef<boolean>;
}

interface RequiredProfile {
  learner_type_id: MaybeRef<string>;
}

interface RequiredApplicationFields {
  requiresApplication: Ref<boolean>;
  session: Ref<RequiredSession>;
  profile: Ref<RequiredProfile>;
}

export const useLearnerInformationDisplaySettings = <T extends MaybeRef<RequiredApplicationFields>>(
  applicationCore: T,
): DisplaySettingsResponse => {
  const application = unref(applicationCore);

  const requiresApplication: boolean = unref(application.requiresApplication);
  const isPersonalized: boolean = unref(application.session.value?.is_personalized);
  const gpaRequired: boolean = unref(application.session.value?.is_gpa_required);
  const learnerTypeId: Ref<string> = ref<string>(unref(application.profile.value?.learner_type_id));

  const updateLearnerTypeId = (newId: MaybeRef<string>): void => {
    learnerTypeId.value = unref(newId);
  };

  const matchesLearnerType = (options: string[]): boolean => {
    return options.includes(learnerTypeId.value);
  };

  const hasHomeInstitution = computed<boolean>(() => {
    return matchesLearnerType([US_COLLEGE_STUDENT_ID, US_GRADUATE_STUDENT_ID]);
  });

  const isUsCollegeStudent = computed<boolean>(() => {
    return matchesLearnerType([US_COLLEGE_STUDENT_ID]);
  });

  const showLearnerTypes = computed<boolean>(() => !isPersonalized);

  const showSchools = computed<boolean>(() => !isPersonalized && hasHomeInstitution.value);

  const showMajorMinors = computed<boolean>(() => matchesLearnerType([US_COLLEGE_STUDENT_ID]));

  const showGpa = computed<boolean>(() => requiresApplication && gpaRequired);

  const showExpectedGraduation = computed<boolean>(() => hasHomeInstitution.value);

  const showLanguages = computed<boolean>(() => requiresApplication);

  const showYearInSchool = computed<boolean>(() => {
    return matchesLearnerType([
      INTERNATIONAL_COLLEGE_STUDENT_ID,
      INTERNATIONAL_HIGH_SCHOOL_STUDENT_ID,
      US_HIGH_SCHOOL_STUDENT_ID,
      US_COLLEGE_STUDENT_ID,
    ]);
  });

  const showSchoolStanding = computed<boolean>(() => !matchesLearnerType([WORKING_PROFESSIONAL_ID]));

  const showPlanToPay = computed<boolean>(() => requiresApplication && isUsCollegeStudent.value);

  const showAddress = computed<boolean>(() => {
    // Just in case the rules change, keep the same format
    return true;
  });

  return {
    isUsCollegeStudent,
    hasHomeInstitution,
    showLearnerTypes,
    showSchools,
    showMajorMinors,
    showGpa,
    showExpectedGraduation,
    showLanguages,
    showYearInSchool,
    showSchoolStanding,
    showPlanToPay,
    showAddress,
    updateLearnerTypeId,
  };
};
